import { useRef, useCallback, useContext } from "react";
import { useAdminSelector } from "../../../../admin/stores/AdminStore";
import { ChatbotStateDispatchContext } from "../utils/chatbotContext";

const MAX_CONNECT_RETRIES = 3;
const RETRY_DELAY_MS = 500;

const useWebSocket = () => {
  const { username, accessToken } = useAdminSelector(s => s.auth);
  const wsRef = useRef<WebSocket | null>(null);
  const retryCountRef = useRef(0);
  const retryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const dispatch = useContext(ChatbotStateDispatchContext);

  const openSocket = useCallback((brand: string, role: string) => {
    if (retryTimerRef.current) {
      clearTimeout(retryTimerRef.current);
      retryTimerRef.current = null;
    }

    const previous = wsRef.current;
    if (previous) {
      previous.onopen = previous.onerror = previous.onclose = previous.onmessage = null;
      previous.close();
    }

    wsRef.current = new WebSocket(`${import.meta.env.VITE_UNLEASHED_AI_WS_URL}`);
    const wsCurrent = wsRef.current;
    let opened = false;
    let failureHandled = false;

    const handleFailure = () => {
      if (failureHandled) return;
      failureHandled = true;

      if (!opened && retryCountRef.current < MAX_CONNECT_RETRIES) {
        retryCountRef.current += 1;
        retryTimerRef.current = setTimeout(() => {
          retryTimerRef.current = null;
          openSocket(brand, role);
        }, RETRY_DELAY_MS);
        return;
      }

      dispatch({ type: "TRANSITION", payload: "CONNECT_FAILURE" });
    };

    if (wsCurrent) {
      wsCurrent.onopen = () => {
        opened = true;
        retryCountRef.current = 0;

        dispatch({ type: "TRANSITION", payload: "CONNECT_SUCCESS" });

        wsCurrent.send(JSON.stringify({
          action: "handshake",
          token: accessToken,
          user_email: username,
          brand: brand,
          role: role,
        }));

      };
      wsCurrent.onerror = handleFailure;

      wsCurrent.onclose = handleFailure;
    }

    wsRef.current.onmessage = (event) => {
      const response = JSON.parse(event.data);
      if (response.action === "get_conversation_history") {
        dispatch({ type: "UPDATE_CONVERSATION_HISTORY", payload: response.historical_messages });
      }

      if (response.action === "get_historical_messages") {
        dispatch({ type: "UPDATE_SELECTED_CONVERSATION_MSSGS", payload: response.current_messages });
      }

      if (response.action === "chat") {
        dispatch({ type: "UPDATE_IS_STREAMING", payload: !response.endOfMessage });
        dispatch({ type: "UPDATE_STREAMING_RESPONSE", payload: response.response });
      }

      if (response.success === true && response.zendesk_link) {
        window.open(response.zendesk_link, "_blank", "noopener,noreferrer");
      }

      if (response.action === "stop_stream") {
        dispatch({ type: "UPDATE_IS_STREAMING", payload: false });
      }

      if (response.action === "error" && response.error_message === "Session timed out.") {
        dispatch({ type: "TRANSITION", payload: "SESSION_TIMEOUT" });
        dispatch({ type: "CLEAR_SELECTED_CONVERSATION" });
      }

      if (response.action === "error") {
        dispatch({ type: "TRANSITION", payload: "CONNECT_FAILURE" });
      }
    };

    return () => {
      if (retryTimerRef.current) {
        clearTimeout(retryTimerRef.current);
        retryTimerRef.current = null;
      }
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, [dispatch, username, accessToken]);

  const connect = useCallback((brand: string, role: string) => {
    retryCountRef.current = 0;
    return openSocket(brand, role);
  }, [openSocket]);

  const startNewConversation = useCallback(
    async (brand: string, role: string) => {
      try {
        if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {

          wsRef.current.send(
            JSON.stringify({
              action: "new_conversation",
              user_email: username,
              brand: brand,
              role: role,
            })
          );
        } else {
          dispatch({ type: "ERROR", payload: "WebSocket is not open" });
        }
      } catch (error) {
        dispatch({ type: "ERROR", payload: `Error sending message:, ${error}` });
      }
    },
    [username, dispatch]
  );

  const submitQuestionToWebSocket = useCallback(
    async (prompt: string, brand: string, role: string) => {
      dispatch({ type: "UPDATE_STREAMING_QUESTION", payload: prompt });
      try {
        if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
          wsRef.current.send(
            JSON.stringify({
              brand: brand,
              role: role,
              prompt: prompt,
              user_email: username,
            })
          );
        } else {
          dispatch({ type: "ERROR", payload: "WebSocket is not open" });
        }
      } catch (error) {
        dispatch({ type: "ERROR", payload: `Error sending message:, ${error}` });
      }
    },
    [username, dispatch]
  );

  const submitFeedback = useCallback(
    async (message_id: string, feedback: boolean) => {
      try {
        if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
          wsRef.current.send(
            JSON.stringify({
              action: "feedback",
              message_id: message_id,
              feedback: feedback,
            })
          );
        } else {
          dispatch({ type: "ERROR", payload: "WebSocket is not open" });
        }
      } catch (error) {
        dispatch({ type: "ERROR", payload: `Error sending message:, ${error}` });
      }
    },
    [dispatch]
  );

  const getConversationHistory = useCallback(
    async (brand: string, role: string) => {
      try {
        if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
          wsRef.current.send(
            JSON.stringify({
              action: "get_conversation_history",
              user_email: username,
              brand: brand,
              role: role
            })
          );
        } else {
          dispatch({ type: "ERROR", payload: "WebSocket is not open" });
        }
      } catch (error) {
        dispatch({ type: "ERROR", payload: `Error sending message:, ${error}` });
      }
    },
    [username, dispatch]
  );

  const getConversationMssgs = useCallback(
    async (conversationId: string) => {
      try {
        if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
          wsRef.current.send(
            JSON.stringify({
              action: "get_historical_messages",
              user_email: username,
              conversation_id: conversationId
            })
          );
        } else {
          dispatch({ type: "ERROR", payload: "WebSocket is not open" });
        }
      } catch (error) {
        dispatch({ type: "ERROR", payload: `Error sending message:, ${error}` });
      }
    },
    [username, dispatch]
  );

  const stopWebSocketStream = useCallback(() => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(
        JSON.stringify({
          action: "stop_stream",
        })
      );
    } else {
      dispatch({ type: "ERROR", payload: "WebSocket is not open" });
    }
  }, [dispatch]);

  const transferToZendesk = useCallback(
    async (brand: string) => {
      try {
        if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
          wsRef.current.send(
            JSON.stringify({
              action: "zendesk",
              brand: brand
            })
          );
        } else {
          dispatch({ type: "ERROR", payload: "WebSocket is not open" });
        }
      } catch (error) {
        dispatch({ type: "ERROR", payload: `Error sending message:, ${error}` });
      }
    },
    [dispatch]
  );

  return { connect, startNewConversation, submitQuestionToWebSocket, submitFeedback, getConversationHistory, getConversationMssgs, stopWebSocketStream, transferToZendesk };
};

export default useWebSocket;